import { useMemo, useState } from 'react';
import { useChatStore } from '../../stores/chat';
import { useAuthStore } from '../../stores/auth';
import { threadsApi, type AppUser } from '../../lib/api';
import Sheet from '../ui/Sheet';
import RoleChip from '../ui/RoleChip';
import { Avatar, PresenceDot } from '../ui/Avatar';
import { Search, Check } from '../../lib/icons';

interface Props {
  threadId: string;
  mode: 'add' | 'remove';
  onClose: () => void;
}

// Built from scratch — no picker/multi-select/bottom-sheet component existed
// before this redesign. Reuses TasksPanel.tsx's AssigneePicker for the
// search + multi-select + chip pattern, and Sheet for the shell.
export default function TeammatePicker({ threadId, mode, onClose }: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const allUsers = useChatStore((s) => s.users);
  const onlineUserIds = useChatStore((s) => s.onlineUserIds);
  const thread = useChatStore((s) => s.threads.find((t) => t.id === threadId));
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingIds = useMemo(
    () => new Set((thread?.participants ?? []).map((p) => p.user_id).filter(Boolean) as string[]),
    [thread?.participants],
  );

  const clientName = thread?.contact?.display_name || thread?.contact?.name || 'the client';

  const candidates: AppUser[] = mode === 'add'
    ? allUsers.filter((u) => u.id !== currentUserId)
    : allUsers.filter((u) => existingIds.has(u.id) && u.id !== currentUserId);

  const filtered = query
    ? candidates.filter((u) => u.name.toLowerCase().includes(query.toLowerCase()))
    : candidates;

  function toggle(id: string) {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function submit() {
    if (selected.length === 0) return;
    setSubmitting(true);
    setError(null);
    const results = await Promise.allSettled(
      selected.map((id) => mode === 'add'
        ? threadsApi.addParticipant(threadId, id)
        : threadsApi.removeParticipant(threadId, id))
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      setError(`${failed} of ${selected.length} ${mode === 'add' ? 'additions' : 'removals'} failed — please retry.`);
      setSubmitting(false);
      // Participants refresh via the `thread:updated` socket event on each success.
    } else {
      onClose();
    }
  }

  return (
    <Sheet title={mode === 'add' ? 'Add teammates' : 'Remove teammates'} onClose={onClose}>
      <div style={{ padding: '4px 18px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {mode === 'add' && (
          <p style={{ fontSize: '0.78rem', color: 'var(--note-label)', lineHeight: 1.5, background: 'var(--note-surface)', border: '1px solid var(--note-border)', borderRadius: 4, padding: '8px 12px' }}>
            They'll see this thread's full history, including internal notes. {clientName} is not told.
          </p>
        )}

        <div style={{ position: 'relative' }}>
          <Search size={14} strokeWidth={1.8} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--shell-xs)' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people…"
            style={{
              width: '100%', boxSizing: 'border-box', background: 'var(--vs2)', border: '1px solid var(--vb)',
              borderRadius: 6, padding: '8px 10px 8px 32px', color: 'var(--text)', fontSize: '0.82rem', outline: 'none',
            }}
          />
        </div>

        {selected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {selected.map((id) => {
              const u = allUsers.find((x) => x.id === id);
              if (!u) return null;
              return (
                <span key={id} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 4px 10px', borderRadius: 20,
                  background: 'var(--accent-tint)', border: '1px solid var(--accent)', fontSize: '0.76rem', color: 'var(--text-primary)',
                }}>
                  {u.name}
                  <span onClick={() => toggle(id)} style={{ cursor: 'pointer', color: 'var(--shell-xs)', lineHeight: 1 }}>×</span>
                </span>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 320, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '16px 4px', fontSize: '0.8rem', color: 'var(--shell-xs)', textAlign: 'center' }}>No matches</div>
          )}
          {filtered.map((u) => {
            const isChecked = selected.includes(u.id);
            const alreadyIn = mode === 'add' && existingIds.has(u.id);
            return (
              <button
                key={u.id}
                onClick={() => { if (!alreadyIn) toggle(u.id); }}
                disabled={alreadyIn}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 6px',
                  background: isChecked ? 'var(--active-bg)' : 'transparent',
                  borderLeft: isChecked ? '2px solid var(--accent)' : '2px solid transparent',
                  border: 'none', borderRadius: 4, textAlign: 'left', width: '100%',
                  cursor: alreadyIn ? 'not-allowed' : 'pointer', opacity: alreadyIn ? 0.45 : 1,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Avatar name={u.name} initialsOverride={u.avatarInitials ?? u.avatar_initials} size={32} />
                  <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
                    <PresenceDot online={onlineUserIds.has(u.id)} />
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>{u.name}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>
                    {alreadyIn ? 'Already in this thread' : onlineUserIds.has(u.id) ? 'Online' : 'Offline'}
                  </div>
                </div>
                <RoleChip role={u.role} />
                {isChecked && <Check size={16} strokeWidth={2} style={{ color: 'var(--green)' }} />}
              </button>
            );
          })}
        </div>

        {error && <div style={{ fontSize: '0.78rem', color: 'var(--red)' }}>{error}</div>}
      </div>

      <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-subtle)' }}>
        <button
          onClick={() => void submit()}
          disabled={selected.length === 0 || submitting}
          style={{
            width: '100%', padding: '10px', borderRadius: 6, border: 'none',
            background: selected.length === 0 || submitting ? 'var(--vb2)' : 'var(--accent)',
            color: selected.length === 0 || submitting ? 'var(--shell-muted)' : 'var(--text-on-accent)',
            fontSize: '0.84rem', fontWeight: 700, cursor: selected.length === 0 || submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting
            ? 'Working…'
            : mode === 'add'
              ? `Add ${selected.length || ''} teammate${selected.length === 1 ? '' : 's'}`.trim()
              : `Remove ${selected.length || ''} teammate${selected.length === 1 ? '' : 's'}`.trim()}
        </button>
      </div>
    </Sheet>
  );
}
